import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { ComboboxCommonProps, ComboboxItem } from '@/components'
import { Combobox } from '@/components'
import { useOnErrorAuto } from '@/utils/hooks'
import { ALL_ISSUE_RISK_ASPECTS_QUERY } from '../queries'
import type {
  AllIssueRiskAspectsQuery,
  AllIssueRiskAspectsQueryVariables,
} from '../types.graphl'

interface IssueRiskAspectComboboxProps extends ComboboxCommonProps<string> {
  value: string | null
  error?: string | null
  clearable?: boolean
  onChange(value: string | null): void
}

export const IssueRiskAspectCombobox: React.FC<
  IssueRiskAspectComboboxProps
> = ({
  value,
  error,
  clearable = true,
  placeholder,
  onChange,

  ...comboboxProps
}) => {
  const translations = useTranslate({
    riskAspect: 'common.risk-aspect',
  })

  const onErrorAuto = useOnErrorAuto()

  const { data } = useQuery<
    AllIssueRiskAspectsQuery,
    AllIssueRiskAspectsQueryVariables
  >(ALL_ISSUE_RISK_ASPECTS_QUERY, {
    onError: onErrorAuto(),
  })

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      (data?.allIssueRiskAspects.edges ?? []).map(({ node }) => ({
        value: node.id,
        label: node.name,
      })),
    [data]
  )

  return (
    <div>
      <Combobox
        {...comboboxProps}
        mode="single"
        value={value ?? ''}
        items={items}
        clearable={clearable}
        placeholder={placeholder ?? translations.riskAspect}
        onChange={id => onChange(id || null)}
      />
      {error && <p className="mt-1 text-xs text-rose-500">{error}</p>}
    </div>
  )
}
